/** Parse HH:MM:SS.ms clip timestamps to seconds (for ReactPlayer seek). */
export function parseClipTimestamp(timeString) {
  if (!timeString || typeof timeString !== 'string') return null;

  const trimmed = timeString.trim();
  const match = trimmed.match(/^(\d{2}):(\d{2}):(\d{2})\.(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const centiseconds = Number(match[4]);

  if ([hours, minutes, seconds, centiseconds].some(n => Number.isNaN(n))) return null;

  const total = hours * 3600 + minutes * 60 + seconds + centiseconds / 100;
  return Number.isFinite(total) && total >= 0 ? total : null;
}

export function isValidClipTimestamp(timeString) {
  return parseClipTimestamp(timeString) != null;
}

/** Format seconds as HH:MM:SS.ms (centiseconds). */
export function formatClipTimestamp(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return null;

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const cs = Math.floor((seconds % 1) * 100);

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
}

/** True when both times are valid and start is strictly before end. */
export function isClipRangeValid(startTime, endTime) {
  if (!startTime?.trim() || !endTime?.trim()) return true;

  const startSec = parseClipTimestamp(startTime);
  const endSec = parseClipTimestamp(endTime);
  if (startSec == null || endSec == null) return false;

  return startSec < endSec;
}

/** Validate a single timestamp update against the paired start/end values. */
export function validateClipTimeUpdate(startTime, endTime, field, newValue) {
  const trimmed = (newValue ?? '').trim();
  if (!trimmed) {
    return { ok: false, error: 'Timestamp cannot be empty' };
  }
  if (!isValidClipTimestamp(trimmed)) {
    return { ok: false, error: 'Use format HH:MM:SS.ms' };
  }

  const nextStart = field === 'startTime' ? trimmed : startTime;
  const nextEnd = field === 'endTime' ? trimmed : endTime;

  if (nextStart?.trim() && nextEnd?.trim() && !isClipRangeValid(nextStart, nextEnd)) {
    return { ok: false, error: 'Start time must be before end time' };
  }

  return { ok: true, value: trimmed };
}

/** Seconds to skip when using ± nudge buttons on clip timestamp fields. */
export const CLIP_SEEK_NUDGE_SECONDS = 5;

/** Shift a valid timestamp by delta seconds (negative = earlier). Clamps at 0. */
export function nudgeClipTimestamp(timeString, deltaSeconds) {
  const sec = parseClipTimestamp(timeString);
  if (sec == null || !Number.isFinite(deltaSeconds)) return null;

  const deltaCs = Math.round(deltaSeconds * 100);
  const baseCs = Math.round(sec * 100);
  const nextCs = Math.max(0, baseCs + deltaCs);

  return formatClipTimestamp(nextCs / 100);
}

export function isRefTimestampField(field) {
  return field === 'refStartTime' || field === 'refEndTime';
}

export function timestampFieldSource(field) {
  return isRefTimestampField(field) ? 'reference' : 'original';
}

/** Append a finished start→end clip, or finish an in-progress start mark. */
export function withFinishedClip(clips, character, url, startTime, endTime) {
  const newClip = { startTime, endTime, mute: true };
  const updatedClips = [...(clips || [])];
  const charIdx = updatedClips.findIndex(c => c.characterName === character);

  if (charIdx === -1) {
    updatedClips.push({
      characterName: character,
      videos: [{ url, vidclips: [newClip] }],
    });
    return updatedClips;
  }

  const charData = { ...updatedClips[charIdx] };
  const vids = [...(charData.videos || [])];
  const vidIdx = vids.findIndex(v => v.url === url);

  if (vidIdx === -1) {
    vids.push({ url, vidclips: [newClip] });
  } else {
    const video = { ...vids[vidIdx] };
    const vidclips = [...(video.vidclips || [])];
    const last = vidclips[vidclips.length - 1];
    if (last && (last.endTime == null || last.endTime === '')) {
      vidclips[vidclips.length - 1] = { ...last, startTime, endTime };
    } else {
      vidclips.push(newClip);
    }
    vids[vidIdx] = { ...video, vidclips };
  }

  charData.videos = vids;
  updatedClips[charIdx] = charData;
  return updatedClips;
}

/** Validate start/end or refStart/refEnd updates on a clip object. */
export function validateAnyClipTimeUpdate(clip, field, newValue) {
  const trimmed = (newValue ?? '').trim();
  if (!trimmed) {
    return { ok: false, error: 'Timestamp cannot be empty' };
  }
  if (!isValidClipTimestamp(trimmed)) {
    return { ok: false, error: 'Use format HH:MM:SS.ms' };
  }

  let startKey = 'startTime';
  let endKey = 'endTime';
  let logicalField = field;

  if (field === 'refStartTime') {
    startKey = 'refStartTime';
    endKey = 'refEndTime';
    logicalField = 'startTime';
  } else if (field === 'refEndTime') {
    startKey = 'refStartTime';
    endKey = 'refEndTime';
    logicalField = 'endTime';
  }

  const startVal = clip?.[startKey];
  const endVal = clip?.[endKey];
  const nextStart = logicalField === 'startTime' ? trimmed : startVal;
  const nextEnd = logicalField === 'endTime' ? trimmed : endVal;

  if (nextStart?.trim() && nextEnd?.trim() && !isClipRangeValid(nextStart, nextEnd)) {
    return { ok: false, error: 'Start time must be before end time' };
  }

  return { ok: true, value: trimmed };
}
